import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Wallet, ChevronDown, ChevronUp, ArrowUpRight } from "lucide-react";

const BAR = { kas: "bg-emerald-500", vendor: "bg-sky-500", kendaraan: "bg-amber-500" };

export function ExpenseSummaryPanel() {
  const [data, setData] = useState(null);
  const [expand, setExpand] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canMaint = ["direktur", "it_admin"].includes(user?.role);
  const linkFor = (key, sub) => {
    if (key === "kas") return "/laporan?tab=kas";
    if (key === "vendor") return "/laporan?tab=vendor";
    if (!canMaint) return "/laporan?tab=kendaraan";
    return `/laporan?tab=perawatan${sub ? `&item=${sub}` : ""}`;
  };

  useEffect(() => {
    api.get("/dashboard/expense-summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <Card className="p-5" data-testid="expense-summary-panel">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="h-4 w-4 text-brand" />
        <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Ringkasan Pengeluaran/Kas</h3>
      </div>
      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total</p>
      <p className="text-2xl font-bold font-mono text-brand mb-5" data-testid="expense-total">{formatRupiah(data.total)}</p>
      <div className="space-y-4">
        {data.level1.map((it) => {
          const isVeh = it.key === "kendaraan";
          return (
            <div key={it.key} data-testid={`expense-cat-${it.key}`}>
              <div className="w-full flex items-center justify-between gap-2 text-sm">
                <span className="font-medium flex items-center gap-1">
                  <button onClick={() => navigate(linkFor(it.key))} data-testid={`expense-cat-link-${it.key}`}
                    className="flex items-center gap-1 hover:text-brand hover:underline">
                    {it.label} <ArrowUpRight className="h-3 w-3 opacity-60" />
                  </button>
                  {isVeh && (
                    <button onClick={() => setExpand((e) => !e)} data-testid="expense-kendaraan-toggle" className="p-0.5 rounded hover:bg-secondary">
                      {expand ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </span>
                <span className="font-mono text-xs text-muted-foreground">{formatRupiah(it.amount)} · {it.pct}%</span>
              </div>
              <div className="mt-1.5 h-2 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full ${BAR[it.key]} rounded-full transition-all duration-500`} style={{ width: `${it.pct}%` }} />
              </div>
              {isVeh && expand && (
                <div className="mt-3 ml-2 space-y-2.5 border-l-2 border-amber-200 pl-3" data-testid="expense-kendaraan-breakdown">
                  {data.kendaraan_breakdown.map((b) => (
                    <div key={b.key} data-testid={`expense-kbd-${b.key}`} onClick={() => navigate(linkFor("kendaraan", b.key))}
                      className="cursor-pointer hover:text-brand">
                      <div className="flex items-center justify-between text-xs">
                        <span>{b.label}</span>
                        <span className="font-mono text-muted-foreground">{formatRupiah(b.amount)} · {b.pct}%</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${b.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
